import { useEffect, useState } from "react";
import { endpoints } from "../lib/api";
import { TrendUp, TrendDown, Wallet, ChartPieSlice, Target, ArrowCounterClockwise } from "@phosphor-icons/react";
import { toast } from "sonner";

const fmt = (n) => (n ?? 0).toLocaleString("en-US", { minimumFractionDigits: 2, maximumFractionDigits: 2 });

export const PortfolioCard = ({ refreshKey = 0, onReset }) => {
  const [p, setP] = useState(null);
  const [busy, setBusy] = useState(null);

  const load = async () => {
    try {
      const { data } = await endpoints.portfolio();
      setP(data);
    } catch (e) {
      console.error(e);
    }
  };

  useEffect(() => {
    load();
    const id = setInterval(load, 5000);
    return () => clearInterval(id);
  }, [refreshKey]);

  const resetMetric = async (metric, label) => {
    if (!window.confirm(`Reset ${label}? This cannot be undone.`)) return;
    setBusy(metric);
    try {
      await endpoints.resetMetric(metric);
      toast.success(`${label} reset`);
      await load();
      onReset?.();
    } catch (e) {
      toast.error(`Failed to reset ${label}`);
    } finally {
      setBusy(null);
    }
  };

  if (!p) return <div className="border border-border p-5 h-full">Loading...</div>;

  const totalPnl = (p.realized_pnl || 0) + (p.unrealized_pnl || 0);
  const totalPct = p.initial_balance ? (totalPnl / p.initial_balance) * 100 : 0;
  const pnlColor = totalPnl >= 0 ? "text-signal-buy" : "text-signal-sell";
  const PnlIcon = totalPnl >= 0 ? TrendUp : TrendDown;
  const profitColor = (p.realized_pnl || 0) >= 0 ? "text-signal-buy" : "text-signal-sell";
  const isLive = p.source === "live";
  const dailyColor = (p.daily_pnl || 0) >= 0 ? "text-signal-buy" : "text-signal-sell";
  const targetProgress = Math.max(0, Math.min(100, p.daily_target_progress_pct || 0));

  return (
    <div data-testid="portfolio-card" className="space-y-3">
      {/* Source & daily target strip */}
      <div className="flex flex-wrap items-center justify-between gap-3 border border-border rounded-sm bg-[#0d0d0d] px-4 py-2.5">
        <div className="flex items-center gap-3">
          <span
            className={`font-mono text-[10px] uppercase tracking-widest px-2 py-1 rounded-sm border ${
              isLive
                ? "border-signal-buy/40 text-signal-buy bg-signal-buy/10"
                : "border-signal-warn/40 text-signal-warn bg-signal-warn/10"
            }`}
            data-testid="equity-source"
          >
            {isLive ? "LIVE · BINANCE FUTURES" : `PAPER (${(p.mode || "paper").toUpperCase()})`}
          </span>
          {p.live_error && (
            <span className="text-[10px] font-mono text-signal-sell" title={p.live_error}>
              Live fetch error — showing paper
            </span>
          )}
        </div>
        <div className="flex items-center gap-3 flex-wrap">
          <span className="font-mono text-[10px] uppercase tracking-widest text-muted-foreground">
            Daily PnL
          </span>
          <span className={`font-mono font-bold text-sm ${dailyColor}`} data-testid="daily-pnl">
            {(p.daily_pnl || 0) >= 0 ? "+" : ""}${fmt(p.daily_pnl || 0)}
          </span>
          {p.daily_target > 0 && (
            <div className="flex items-center gap-2">
              <span className="font-mono text-[10px] text-signal-buy/80">
                🎯 ${fmt(p.daily_target)}
              </span>
              <div className="w-20 h-1.5 bg-[#151515] relative overflow-hidden rounded-sm">
                <div
                  className={`absolute top-0 left-0 h-full ${
                    p.daily_target_reached ? "bg-signal-warn" : "bg-signal-buy"
                  }`}
                  style={{ width: `${Math.max(0, Math.min(100, p.daily_target_progress_pct || 0))}%` }}
                />
              </div>
              <span className={`font-mono text-[10px] ${p.daily_target_reached ? "text-signal-warn" : "text-muted-foreground"}`}>
                {(p.daily_target_progress_pct || 0).toFixed(0)}%
                {p.daily_target_reached && " HIT"}
              </span>
            </div>
          )}
          {p.daily_loss_limit > 0 && (
            <div className="flex items-center gap-2">
              <span className="font-mono text-[10px] text-signal-sell/80">
                🛑 -${fmt(p.daily_loss_limit)}
              </span>
              <div className="w-20 h-1.5 bg-[#151515] relative overflow-hidden rounded-sm">
                <div
                  className="absolute top-0 left-0 h-full bg-signal-sell"
                  style={{ width: `${Math.max(0, Math.min(100, p.daily_loss_progress_pct || 0))}%` }}
                />
              </div>
              <span className={`font-mono text-[10px] ${p.daily_loss_reached ? "text-signal-sell" : "text-muted-foreground"}`}>
                {(p.daily_loss_progress_pct || 0).toFixed(0)}%
                {p.daily_loss_reached && " HIT"}
              </span>
            </div>
          )}
          {!p.daily_target && !p.daily_loss_limit && (
            <span className="font-mono text-[10px] text-muted-foreground">
              No daily limits set
            </span>
          )}
        </div>
      </div>

      <div className="grid grid-cols-2 lg:grid-cols-5 gap-3">
      {/* Available */}
      <div className="border border-border p-4 rounded-sm bg-[#0d0d0d]">
        <div className="flex items-center gap-2 text-muted-foreground">
          <Wallet size={14} />
          <span className="font-mono text-[10px] uppercase tracking-widest">Available</span>
        </div>
        <div className="font-mono font-bold text-2xl mt-2" data-testid="wallet-balance">
          ${fmt(p.balance)}
        </div>
        <div className="font-mono text-[11px] text-muted-foreground mt-1">
          Initial ${fmt(p.initial_balance)}
        </div>
      </div>

      {/* Equity */}
      <div className="border border-border p-4 rounded-sm bg-[#0d0d0d]">
        <div className="flex items-center gap-2 text-muted-foreground">
          <ChartPieSlice size={14} />
          <span className="font-mono text-[10px] uppercase tracking-widest">Equity</span>
        </div>
        <div className="font-mono font-bold text-2xl mt-2" data-testid="equity">
          ${fmt(p.equity)}
        </div>
        <div className="font-mono text-[11px] text-muted-foreground mt-1">
          Margin locked ${fmt(p.open_margin)}
        </div>
      </div>

      {/* Profit (Realized) */}
      <MetricCard
        label="Profit (Realized)"
        icon={<TrendUp size={14} />}
        value={`${(p.realized_pnl || 0) >= 0 ? "+" : ""}$${fmt(p.realized_pnl || 0)}`}
        color={profitColor}
        onReset={() => resetMetric("pnl", "Realized Profit")}
        busy={busy === "pnl"}
        testid="metric-profit"
        subLabel={`Unrealized $${fmt(p.unrealized_pnl || 0)}`}
      />

      {/* Total PnL */}
      <MetricCard
        label="Total PnL"
        icon={<PnlIcon size={14} />}
        value={`${totalPnl >= 0 ? "+" : ""}$${fmt(totalPnl)}`}
        color={pnlColor}
        onReset={() => resetMetric("pnl", "Total P&L")}
        busy={busy === "pnl"}
        testid="metric-totalpnl"
        subLabel={`${totalPct >= 0 ? "+" : ""}${totalPct.toFixed(2)}%`}
        subColor={pnlColor}
      />

      {/* Win Rate */}
      <MetricCard
        label="Win Rate"
        icon={<Target size={14} />}
        value={`${p.win_rate.toFixed(1)}%`}
        onReset={() => resetMetric("winrate", "Win Rate")}
        busy={busy === "winrate"}
        testid="metric-winrate"
        subLabel={
          <>
            <span className="text-signal-buy">{p.trades_won}W</span>
            {" / "}
            <span className="text-signal-sell">{p.trades_lost}L</span>
          </>
        }
      />
      </div>
    </div>
  );
};

const MetricCard = ({ label, icon, value, color = "text-white", onReset, busy, testid, subLabel, subColor }) => (
  <div className="border border-border p-4 rounded-sm bg-[#0d0d0d] relative group">
    <button
      data-testid={`${testid}-reset-btn`}
      onClick={onReset}
      disabled={busy}
      title={`Reset ${label}`}
      className={`absolute top-2 right-2 p-1 rounded-sm text-muted-foreground hover:text-signal-warn hover:bg-signal-warn/10 transition-opacity ${
        busy ? "opacity-50" : "opacity-0 group-hover:opacity-100"
      }`}
    >
      <ArrowCounterClockwise size={14} weight="bold" className={busy ? "animate-spin" : ""} />
    </button>
    <div className="flex items-center gap-2 text-muted-foreground">
      {icon}
      <span className="font-mono text-[10px] uppercase tracking-widest">{label}</span>
    </div>
    <div className={`font-mono font-bold text-2xl mt-2 ${color}`} data-testid={testid}>
      {value}
    </div>
    <div className={`font-mono text-[11px] mt-1 ${subColor || "text-muted-foreground"}`}>
      {subLabel}
    </div>
  </div>
);

export default PortfolioCard;
